import { css } from '@emotion/react';
import {
  type FC, type ReactNode, useCallback,
  useMemo,
} from 'react';
import { AlternatingStripesComponent } from '~/_shared/baseComponents/alternatingStripes/alternatingStripes';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { MetricProps } from '~/_shared/utils/metric/metrics.types';
import { useGetMetricRegion } from '~/_shared/utils/metric/useGetMetricRegion';
import { type MetricsResults } from '../../utils/metric/useMetricData.helpers';
import { MetricsListItemComponent } from './item/metricsListItem.component';
import { groupMetricsDataByColumn } from './metricsList.helpers';

const rootStyle = (theme: Theme) => css({
  width: '100%',
  color: theme.textColors.primary,
});

const metricsWrapperStyle = (theme: Theme) => css({
  borderBottom: `1px solid ${theme.borderColors.primary}`,
  '&:last-of-type': {
    border: 'none',
  },
});

type MetricsListProps = Readonly<{
  additionalItemsTop?: ReactNode;
  metrics: MetricsResults;
  boundaryIsOptimizedTerritoriesTrial?: boolean;
}>;

export const MetricsListComponent: FC<MetricsListProps> = props => {
  const [t] = useTranslation();

  const { spreadsheetColumnMetrics, demographicMetrics } = props.metrics;

  const [firstTwoMetricsDataGroupedByColumn, restOfMetricsDataGroupedByColumn] = useMemo(() => {
    const groupedMetrics = groupMetricsDataByColumn(spreadsheetColumnMetrics.metricList, t);
    return [groupedMetrics.slice(0, 2), groupedMetrics.slice(2)];
  }, [spreadsheetColumnMetrics, t]);

  const { getMetricRegion } = useGetMetricRegion();

  const renderMetricName = useCallback((metric: MetricProps) => {
    const metricRegion = metric.demographicId ? getMetricRegion(metric.demographicId) : undefined;
    if (metricRegion) {
      return `${metric.name} (${metricRegion})`;
    }

    return metric.name;
  }, [getMetricRegion]);

  return (
    <div css={rootStyle}>
      <AlternatingStripesComponent css={metricsWrapperStyle}>
        {props.additionalItemsTop}

        {demographicMetrics.length > 0 && (
          demographicMetrics.map((metric, index) => (
            <MetricsListItemComponent
              key={index}
              name={renderMetricName(metric)}
              single={{
                ...metric,
              }}
              showApproximateSymbol
              blurMetrics={props.boundaryIsOptimizedTerritoriesTrial}
              inferenceSource={metric.inferenceSource}
            />
          ))
        )}

        {firstTwoMetricsDataGroupedByColumn.map((metric) => (
          <MetricsListItemComponent
            key={metric.name + metric.subheader}
            {...metric}
          />
        ))}
        {restOfMetricsDataGroupedByColumn.map((metric) => (
          <MetricsListItemComponent
            key={metric.name + metric.subheader}
            blurMetrics={props.boundaryIsOptimizedTerritoriesTrial}
            {...metric}
          />
        ))}
      </AlternatingStripesComponent>
    </div>
  );
};
